// src/admin/CatechistSubstituteManager.js - Phân công phụ lớp hiển thị toàn bộ GLV với Select Checkbox
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import {
  subscribeAllSubstituteAssignments,
  saveSubstituteAssignment,
  deleteSubstituteAssignment
} from '../FirebaseService';
import * as XLSX from 'xlsx';
import './CatechistSubstituteManager.css';

// Component Select Checkbox Dropdown cho từng Giáo lý viên
const AssistingClassesSelectCheckbox = ({
  catechist,
  assignedClasses = [],
  groupedClasses = {},
  classMap = {},
  onToggleClass,
  isUpdating
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Đóng dropdown khi bấm ra ngoài
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const assignedClassIds = useMemo(() => {
    return assignedClasses.map((item) => (typeof item === 'string' ? item : item.classId));
  }, [assignedClasses]);

  return (
    <div className="csub-multiselect-container" ref={dropdownRef}>
      {/* Nút trigger hiển thị các lớp đã chọn hoặc nút bấm thêm */}
      <button
        type="button"
        onClick={() => !isUpdating && setIsOpen(!isOpen)}
        disabled={isUpdating}
        className={`csub-multiselect-trigger ${assignedClassIds.length > 0 ? 'has-classes' : ''}`}
        title="Bấm để chọn/bỏ chọn các lớp đi phụ"
      >
        <div className="csub-trigger-content">
          {assignedClassIds.length === 0 ? (
            <span className="csub-empty-select-label">+ Chọn lớp phụ ▾</span>
          ) : (
            <div className="csub-selected-pills-list">
              {assignedClasses.map((cls) => {
                const cId = typeof cls === 'string' ? cls : cls.classId;
                const cName = typeof cls === 'object' ? cls.className : (classMap[cId] || cId);
                return (
                  <span key={cId} className="csub-selected-pill">
                    🤝 {cName}
                  </span>
                );
              })}
              <span className="csub-arrow-down">▾</span>
            </div>
          )}
        </div>
      </button>

      {/* Menu thả xuống chứa danh sách Checkbox phân theo Khối */}
      {isOpen && (
        <div className="csub-dropdown-menu">
          <div className="csub-dropdown-header">
            <span>Chọn lớp phụ cho <strong>{catechist.fullName}</strong>:</span>
            <button
              type="button"
              className="csub-dropdown-close-btn"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>

          <div className="csub-dropdown-scrollable">
            {Object.entries(groupedClasses).map(([groupKey, groupData]) => {
              const groupName = Array.isArray(groupData)
                ? groupData[0]?.group || 'Khác'
                : groupData.groupName || 'Khác';
              const classes = Array.isArray(groupData) ? groupData : groupData.classes || [];

              return (
                <div key={groupKey} className="csub-group-block">
                  <div className="csub-group-title">🏷️ Khối: {groupName}</div>
                  <div className="csub-group-items">
                    {classes.map((cls) => {
                      const isChecked = assignedClassIds.includes(cls.id);
                      const isPrimaryClass =
                        catechist.className &&
                        (cls.name === catechist.className || cls.id === catechist.className);

                      return (
                        <label
                          key={cls.id}
                          className={`csub-checkbox-option ${isChecked ? 'checked' : ''} ${
                            isPrimaryClass ? 'primary-disabled' : ''
                          }`}
                          title={isPrimaryClass ? 'Đây là lớp chính của GLV' : ''}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isPrimaryClass || isUpdating}
                            onChange={() => onToggleClass(catechist, cls.id, cls.name)}
                          />
                          <span className="csub-checkbox-text">
                            {cls.name}
                            {isPrimaryClass && <em className="csub-primary-hint"> (Lớp chính)</em>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const CatechistSubstituteManager = () => {
  const { currentYearId, catechistsData, classMap, allBlocks } = useContext(StudentContext);
  const outletContext = useOutletContext() || {};
  const sidebarSelectedClass = outletContext.selectedClassForOutlet || 'all';

  const [assignmentsMap, setAssignmentsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubStatus, setFilterSubStatus] = useState('ALL'); // 'ALL' | 'ASSIGNED' | 'UNASSIGNED'
  const [updatingUids, setUpdatingUids] = useState({});

  // 1. Lắng nghe toàn bộ dữ liệu phân công phụ lớp theo thời gian thực
  useEffect(() => {
    if (!currentYearId) return;
    setLoading(true);
    const unsubscribe = subscribeAllSubstituteAssignments(currentYearId, (list) => {
      const map = {};
      list.forEach((item) => {
        map[item.catechistUid || item.id] = item;
      });
      setAssignmentsMap(map);
      setLoading(false);
    });
    return () => unsubscribe && unsubscribe();
  }, [currentYearId]);

  // Gom nhóm danh mục lớp theo Khối
  const groupedClasses = useMemo(() => {
    if (allBlocks && Object.keys(allBlocks).length > 0) {
      return allBlocks;
    }
    const fallback = { chung: [] };
    Object.entries(classMap || {}).forEach(([id, name]) => {
      fallback.chung.push({ id, name, group: 'Lớp học' });
    });
    return fallback;
  }, [allBlocks, classMap]);

  // 🌟 TOÀN BỘ GIÁO LÝ VIÊN ĐANG HOẠT ĐỘNG
  const activeCatechists = useMemo(() => {
    return (catechistsData || []).filter((c) => (c.status || 'active') !== 'inactive');
  }, [catechistsData]);

  // 2. Xử lý Toggle Lớp phụ trực tiếp qua Checkbox
  const handleToggleClass = async (catechist, classId, className) => {
    if (!currentYearId || !catechist.uid) return;

    setUpdatingUids((prev) => ({ ...prev, [catechist.uid]: true }));
    try {
      const currentAssignment = assignmentsMap[catechist.uid];
      const existingClasses = currentAssignment?.assignedClasses || [];

      const exists = existingClasses.some((item) => {
        const cId = typeof item === 'string' ? item : item.classId;
        return cId === classId;
      });

      let updatedClasses;
      if (exists) {
        // Bỏ chọn lớp
        updatedClasses = existingClasses.filter((item) => {
          const cId = typeof item === 'string' ? item : item.classId;
          return cId !== classId;
        });
      } else {
        // Tích chọn thêm lớp
        updatedClasses = [...existingClasses, { classId, className: className || classMap[classId] || classId }];
      }

      const adminName = localStorage.getItem('catechistFullName') || 'Admin';

      if (updatedClasses.length === 0) {
        // Nếu không còn lớp phụ nào -> Xóa bản ghi
        await deleteSubstituteAssignment(currentYearId, catechist.uid);
      } else {
        // Lưu danh sách lớp phụ mới lên Firestore
        const payload = {
          holyName: catechist.holyName || '',
          fullName: catechist.fullName || '',
          idCode: catechist.idCode || '',
          primaryClassName: catechist.className || 'Chưa xếp lớp',
          assignedClasses: updatedClasses,
          updatedBy: adminName
        };
        await saveSubstituteAssignment(currentYearId, catechist.uid, payload);
      }
    } catch (err) {
      console.error('Lỗi khi phân công lớp phụ:', err);
      alert('❌ Lỗi: ' + err.message);
    } finally {
      setUpdatingUids((prev) => ({ ...prev, [catechist.uid]: false }));
    }
  };

  // 3. Lọc và Sắp Xếp Toàn Bộ Giáo Lý Viên
  const displayedCatechists = useMemo(() => {
    return activeCatechists.filter((c) => {
      const assignment = assignmentsMap[c.uid];
      const assignedClasses = assignment?.assignedClasses || [];
      const hasSubClasses = assignedClasses.length > 0;

      // Lọc theo trạng thái phụ lớp
      if (filterSubStatus === 'ASSIGNED' && !hasSubClasses) return false;
      if (filterSubStatus === 'UNASSIGNED' && hasSubClasses) return false;

      // Lọc theo Sidebar chọn lớp
      if (sidebarSelectedClass && sidebarSelectedClass !== 'all') {
        const primaryMatch =
          c.className === (classMap[sidebarSelectedClass] || sidebarSelectedClass);
        const assignedMatch = assignedClasses.some((cls) => {
          const cId = typeof cls === 'string' ? cls : cls.classId;
          const cName = typeof cls === 'object' ? cls.className : classMap[cId];
          return (
            cId === sidebarSelectedClass ||
            cName === (classMap[sidebarSelectedClass] || sidebarSelectedClass)
          );
        });

        if (!primaryMatch && !assignedMatch) return false;
      }

      // Lọc theo ô tìm kiếm
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        const fullName = (c.fullName || '').toLowerCase();
        const holyName = (c.holyName || '').toLowerCase();
        const idCode = (c.idCode || '').toLowerCase();
        const primary = (c.className || '').toLowerCase();
        const assignedNames = assignedClasses
          .map((item) => (typeof item === 'object' ? item.className : classMap[item]))
          .join(' ')
          .toLowerCase();

        return (
          fullName.includes(term) ||
          holyName.includes(term) ||
          idCode.includes(term) ||
          primary.includes(term) ||
          assignedNames.includes(term)
        );
      }

      return true;
    });
  }, [activeCatechists, assignmentsMap, filterSubStatus, sidebarSelectedClass, searchTerm, classMap]);

  // Thống kê nhanh
  const stats = useMemo(() => {
    let assignedCount = 0;
    let totalAssignedClassLinks = 0;

    activeCatechists.forEach((c) => {
      const classes = assignmentsMap[c.uid]?.assignedClasses || [];
      if (classes.length > 0) {
        assignedCount++;
        totalAssignedClassLinks += classes.length;
      }
    });

    return {
      totalCatechists: activeCatechists.length,
      assignedCount,
      totalAssignedClassLinks
    };
  }, [activeCatechists, assignmentsMap]);

  // Xuất Excel
  const handleExportExcel = () => {
    if (displayedCatechists.length === 0) {
      alert('Không có dữ liệu để xuất Excel.');
      return;
    }

    const data = [
      ['STT', 'Mã ID', 'Tên Thánh', 'Họ và Tên', 'Lớp Chính', 'Các Lớp Đi Phụ (Được phép điểm danh)']
    ];

    displayedCatechists.forEach((c, idx) => {
      const assignedClasses = assignmentsMap[c.uid]?.assignedClasses || [];
      const classNamesStr = assignedClasses
        .map((item) => (typeof item === 'object' ? item.className : classMap[item] || item))
        .join(', ');

      data.push([
        idx + 1,
        c.idCode || '',
        c.holyName || '',
        c.fullName || '',
        c.className || 'Chưa xếp lớp',
        classNamesStr || 'Không'
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'PhanCongPhuLop');
    XLSX.writeFile(wb, `PhanCong_PhuLop_${currentYearId || 'NienKhoa'}.xlsx`);
  };

  return (
    <div className="csub-container">
      {/* HEADER */}
      <div className="csub-header">
        <div>
          <h2 className="csub-title">🤝 Phân Công Giáo Lý Viên Đi Phụ Lớp</h2>
          <p className="csub-subtitle">
            Tích chọn trực tiếp các lớp đi phụ cho Giáo lý viên. GLV được phân công phụ lớp nào sẽ có quyền chuyển lớp và điểm danh thiếu nhi tại <strong>QRScannerClass</strong> của lớp đó.
          </p>
        </div>
        <div className="csub-header-actions">
          <button type="button" onClick={handleExportExcel} className="csub-btn csub-btn-excel">
            📥 Xuất Excel ({displayedCatechists.length})
          </button>
        </div>
      </div>

      {/* THẺ THỐNG KÊ NHANH */}
      <div className="csub-stats-grid">
        <div className="csub-stat-card blue">
          <span className="csub-stat-num">{stats.totalCatechists}</span>
          <span className="csub-stat-lbl">Tổng Số Giáo Lý Viên</span>
        </div>
        <div className="csub-stat-card green">
          <span className="csub-stat-num">{stats.assignedCount}</span>
          <span className="csub-stat-lbl">GLV Đang Được Phân Công Phụ Lớp</span>
        </div>
        <div className="csub-stat-card orange">
          <span className="csub-stat-num">{stats.totalAssignedClassLinks}</span>
          <span className="csub-stat-lbl">Tổng Lượt Lớp Đang Được Phụ</span>
        </div>
      </div>

      {/* THANH CÔNG CỤ TÌM KIẾM & BỘ LỌC */}
      <div className="csub-toolbar-card">
        <div className="csub-search-box">
          <input
            type="text"
            placeholder="🔎 Tìm theo tên GLV, ID, lớp chính hoặc lớp phụ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="csub-search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="csub-btn-clear" title="Xóa tìm kiếm">
              ✕
            </button>
          )}
        </div>

        <div className="csub-filter-group">
          <label className="csub-label">Trạng thái:</label>
          <select
            value={filterSubStatus}
            onChange={(e) => setFilterSubStatus(e.target.value)}
            className="csub-select"
          >
            <option value="ALL">-- Tất cả GLV ({activeCatechists.length}) --</option>
            <option value="ASSIGNED">🤝 Đang có phụ lớp ({stats.assignedCount})</option>
            <option value="UNASSIGNED">⚪ Chưa phân công phụ ({stats.totalCatechists - stats.assignedCount})</option>
          </select>
        </div>

        <span className="csub-count-badge">
          Hiển thị: <strong>{displayedCatechists.length}</strong> GLV
        </span>
      </div>

      {/* BẢNG TOÀN BỘ GIÁO LÝ VIÊN VỚI SELECT CHECKBOX */}
      <div className="csub-table-wrapper">
        <table className="csub-table">
          <thead>
            <tr>
              <th className="csub-text-center" style={{ width: '45px' }}>STT</th>
              <th style={{ width: '95px' }}>Mã ID</th>
              <th>Tên Thánh & Họ và Tên</th>
              <th style={{ width: '160px' }}>Lớp Chính</th>
              <th>Các Lớp Đi Phụ (Tích chọn bằng checkbox để phân công)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="csub-empty-cell">
                  ⏳ Đang tải toàn bộ danh sách giáo lý viên...
                </td>
              </tr>
            ) : displayedCatechists.length === 0 ? (
              <tr>
                <td colSpan="5" className="csub-empty-cell">
                  {searchTerm
                    ? `Không tìm thấy giáo lý viên nào khớp với "${searchTerm}".`
                    : 'Không có dữ liệu giáo lý viên.'}
                </td>
              </tr>
            ) : (
              displayedCatechists.map((catechist, index) => {
                const assignment = assignmentsMap[catechist.uid];
                const assignedClasses = assignment?.assignedClasses || [];
                const isUpdating = Boolean(updatingUids[catechist.uid]);

                return (
                  <tr key={catechist.uid}>
                    <td className="csub-text-center">{index + 1}</td>
                    <td className="csub-code-cell">{catechist.idCode || '-'}</td>
                    <td>
                      <span className="csub-holy-name">{catechist.holyName}</span>
                      <strong className="csub-full-name">{catechist.fullName}</strong>
                    </td>
                    <td>
                      <span className="csub-primary-class-tag">
                        {catechist.className || 'Chưa xếp lớp'}
                      </span>
                    </td>
                    <td>
                      {/* 🌟 CỘT SELECT CHECKBOX PHỤ LỚP TRỰC TIẾP 🌟 */}
                      <AssistingClassesSelectCheckbox
                        catechist={catechist}
                        assignedClasses={assignedClasses}
                        groupedClasses={groupedClasses}
                        classMap={classMap}
                        onToggleClass={handleToggleClass}
                        isUpdating={isUpdating}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CatechistSubstituteManager;